import { useEffect, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { apiPatch, apiPost, getSelectedOrgId, type Subscriber } from "../api";
import { Icons } from "../components/icons";
import { useAuth } from "../auth";
import { EmailPreview } from "../components/EmailPreview";
import {
  useGetSubscribersQuery,
  useDeleteSubscriberMutation,
  useTestSubscriberMutation,
} from "../store/subscribers";
import { useGetServicesQuery, useServiceSlugMap } from "../store/services";
import { useGetOrganizationsQuery } from "../store/organizations";

/**
 * Canales soportados end-to-end: email y slack (incoming webhook). Son
 * independientes — un suscriptor puede tener uno, el otro, o ambos. SMS y
 * webhook genérico siguen en backlog y no se muestran.
 *
 * `org_id`: la organización en la que se va a crear el suscriptor. El form
 * permite cambiarla SIN tocar el selector global del header — así el contexto
 * del dialog no se pierde. Al submit se manda como header X-Org-Id (override).
 */
type DraftSubscriber = {
  id?: string;
  email: string;
  channels: string[];
  slack_webhook_url: string;
  severity_min: string;
  service_ids: string[] | null; // null = todos los servicios de la org
  org_id: string;
};

export default function Subscribers() {
  const { user } = useAuth();
  // RTK Query: cache compartido. Mutaciones invalidan tags y la lista se refresca sola.
  const { data: subs = [], refetch: refetchSubs } = useGetSubscribersQuery();
  const slugMap = useServiceSlugMap();
  const [deleteSubscriber] = useDeleteSubscriberMutation();
  const [testSubscriber] = useTestSubscriberMutation();
  // Platform admins necesitan la lista completa de orgs para el selector del form.
  // Para users normales se omite (las orgs accesibles vienen en user.available_orgs).
  const { data: allOrgsList = [] } = useGetOrganizationsQuery(undefined, {
    skip: !user?.is_platform_admin,
  });

  const [showInfo, setShowInfo] = useState(false);
  const [draft, setDraft] = useState<DraftSubscriber | null>(null); // null = form cerrado
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [previewSub, setPreviewSub] = useState<Subscriber | null>(null);

  // Pool de orgs elegibles en el form: si es platform admin → todas las del sistema;
  // si no → available_orgs del JWT del usuario.
  const orgOptions = user?.is_platform_admin
    ? allOrgsList.map((o) => ({ id: o.id, name: o.name }))
    : (user?.available_orgs ?? []).map((o) => ({ id: o.org_id, name: o.name }));

  // Default sensible al abrir el form: la org seleccionada en el header si hay,
  // si no la primera disponible.
  const defaultOrgId = getSelectedOrgId() ?? orgOptions[0]?.id ?? "";

  // Cuando el usuario cambia de org en el header, RTK Query no se entera solo
  // (el cache está keyed por endpoint+args, no por header X-Org-Id). Forzamos refetch.
  useEffect(() => {
    function onOrgChanged() {
      refetchSubs();
    }
    window.addEventListener("sovra:org-changed", onOrgChanged);
    return () => window.removeEventListener("sovra:org-changed", onOrgChanged);
  }, [refetchSubs]);

  // Nombre legible de la org actual — usado en el preview del email (botón ✉).
  // Toma el primer match en orgOptions; si no, fallback.
  function orgNameOf(orgId: string | null | undefined): string {
    if (!orgId) return "tu organización";
    return orgOptions.find((o) => o.id === orgId)?.name ?? "tu organización";
  }

  // ───── Crear / editar ────────────────────────────────────────────────────
  function openCreate() {
    if (!defaultOrgId) {
      alert("No tenés organizaciones disponibles para crear suscriptores.");
      return;
    }
    setDraft({
      email: "",
      channels: ["email"],
      slack_webhook_url: "",
      severity_min: "minor",
      service_ids: null,
      org_id: defaultOrgId,
    });
    setError(null);
  }

  function openEdit(s: Subscriber) {
    setDraft({
      id: s.id,
      email: s.email ?? "",
      // El UI solo togglea email/slack; si el subscriber tiene webhook/sms
      // (creado por API) quedan intactos en el array y se preservan al guardar.
      channels: s.channels,
      slack_webhook_url: s.slack_webhook_url ?? "",
      severity_min: s.severity_min,
      service_ids: s.service_ids,
      org_id: s.org_id ?? defaultOrgId,
    });
    setError(null);
  }

  async function submitDraft(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    setSubmitting(true);
    setError(null);
    try {
      const hasEmail = draft.channels.includes("email");
      const hasSlack = draft.channels.includes("slack");
      const payload: Record<string, unknown> = {
        email: hasEmail ? draft.email : null,
        channels: draft.channels,
        slack_webhook_url: hasSlack ? draft.slack_webhook_url : null,
        severity_min: draft.severity_min,
        service_ids: draft.service_ids, // null = todos, array = filtrado
      };

      // OJO: pasamos `orgId` como override del header X-Org-Id solo para esta
      // request. Así el selector del header (que puede decir "Todas") queda
      // intacto y el subscriber se crea exactamente en la org elegida en el form.
      const opts = { orgId: draft.org_id };

      // NOTA: usamos las funciones apiPatch/apiPost legacy aquí porque RTK Query
      // no tiene un mecanismo de orgId-override per-request todavía. La invalidación
      // del tag "Subscribers" se hace manual via refetch.
      if (draft.id) {
        await apiPatch(`/v1/subscribers/${draft.id}`, payload, opts);
      } else {
        await apiPost<Subscriber>("/v1/subscribers", payload, opts);
      }
      setDraft(null);
      refetchSubs();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("¿Eliminar este suscriptor?")) return;
    try {
      await deleteSubscriber(id).unwrap();
      // Tag invalidation refresca la lista automáticamente.
    } catch (err) {
      alert("No se pudo eliminar: " + (err instanceof Error ? err.message : "error"));
    }
  }

  async function sendTest(s: Subscriber) {
    setTestingId(s.id);
    try {
      await testSubscriber(s.id).unwrap();
      alert(`Notificación de prueba enviada a ${s.email ?? "el suscriptor"}.\n\nSi no llega, revisá la verificación o el spam.`);
    } catch (err) {
      alert("No se pudo enviar el test: " + (err instanceof Error ? err.message : "error"));
    } finally {
      setTestingId(null);
    }
  }

  // ───── UI ────────────────────────────────────────────────────────────────
  return (
    <>
      <header className="page-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1>Suscriptores</h1>
            <button
              type="button"
              className="btn btn-icon btn-ghost"
              onClick={() => setShowInfo(true)}
              title="¿Qué es esto?"
              style={{ width: 26, height: 26 }}
            >
              <Icons.Info size={14} />
            </button>
          </div>
          <div className="sub">
            Destinatarios internos que reciben notificaciones cuando se abre o resuelve un incidente.
          </div>
        </div>
        <button
          className="btn btn-primary"
          type="button"
          onClick={openCreate}
          disabled={orgOptions.length === 0}
          title={
            orgOptions.length === 0
              ? "No hay organizaciones disponibles para crear suscriptores"
              : undefined
          }
        >
          <Icons.Plus size={13} sw={2.2} />
          Nuevo suscriptor
        </button>
      </header>

      <div className="page-content" style={{ display: "grid", gap: 18 }}>
        {draft && (
          <SubscriberForm
            draft={draft}
            setDraft={setDraft}
            orgOptions={orgOptions}
            onSubmit={submitDraft}
            onCancel={() => setDraft(null)}
            submitting={submitting}
            error={error}
          />
        )}

        <section className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Destinatario</th>
                <th style={{ width: 240 }}>Servicios notificados</th>
                <th style={{ width: 130 }}>Severidad mín.</th>
                <th style={{ width: 120 }}>Estado</th>
                <th style={{ width: 170, textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id}>
                  <td style={{ color: "var(--fg-1)", fontWeight: 500 }}>
                    <div>{s.email ?? "Canal de Slack"}</div>
                    <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                      {s.channels.includes("email") && (
                        <span className="svc-chip" title="Recibe emails">
                          <Icons.Mail size={10} /> email
                        </span>
                      )}
                      {s.channels.includes("slack") && (
                        <span className="svc-chip" title="Postea en un canal de Slack al abrirse un incidente">
                          <Icons.Send size={10} /> slack
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: "var(--fg-2)" }}>
                    {s.service_ids == null || s.service_ids.length === 0 ? (
                      <span className="svc-chip" title="Recibe notificaciones de todos los servicios de la org">
                        <span className="dot status-dot ok" />
                        Todos
                      </span>
                    ) : (
                      <span title={s.service_ids.map((id) => slugMap[id] ?? id).join(", ")}>
                        {s.service_ids.length} servicio{s.service_ids.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`sev-badge sev-${s.severity_min}`}>{s.severity_min}</span>
                  </td>
                  <td>
                    {s.channels.includes("email") ? (
                      <span className={`badge badge-${s.verified ? "ok" : "degraded"}`}>
                        <span className="dot" style={{ background: "currentColor" }} />
                        {s.verified ? "Verificado" : "Pendiente"}
                      </span>
                    ) : (
                      <span style={{ color: "var(--fg-4)", fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 4 }}>
                      <button
                        className="btn btn-icon btn-ghost"
                        type="button"
                        onClick={() => setPreviewSub(s)}
                        disabled={!s.channels.includes("email")}
                        title={
                          s.channels.includes("email")
                            ? "Ver preview del correo"
                            : "Solo disponible para el canal email"
                        }
                      >
                        <Icons.Mail size={13} />
                      </button>
                      <button
                        className="btn btn-icon btn-ghost"
                        type="button"
                        onClick={() => sendTest(s)}
                        disabled={testingId === s.id || !s.channels.includes("email")}
                        title={
                          s.channels.includes("email")
                            ? "Enviar notificación de prueba"
                            : "Solo disponible para el canal email"
                        }
                      >
                        <Icons.Send size={13} />
                      </button>
                      <button
                        className="btn btn-icon btn-ghost"
                        type="button"
                        onClick={() => openEdit(s)}
                        title="Editar"
                      >
                        <Icons.Pencil size={13} />
                      </button>
                      <button
                        className="btn btn-icon btn-ghost"
                        type="button"
                        onClick={() => remove(s.id)}
                        title="Eliminar"
                        style={{ color: "var(--st-major)" }}
                      >
                        <Icons.Trash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {subs.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 0 }}>
                    <div className="empty" style={{ padding: 40 }}>
                      <div className="ico"><Icons.Users size={20} /></div>
                      <div>Sin suscriptores todavía.</div>
                      <div className="text-sm" style={{ marginTop: 6 }}>
                        Agregá uno para empezar a recibir notificaciones de incidentes.
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      {showInfo && <InfoModal onClose={() => setShowInfo(false)} />}
      {previewSub && (
        <EmailPreview
          subscriber={previewSub}
          orgName={orgNameOf(previewSub.org_id)}
          onClose={() => setPreviewSub(null)}
        />
      )}
    </>
  );
}

// ─── Sub-form ────────────────────────────────────────────────────────────────

function SubscriberForm({
  draft,
  setDraft,
  orgOptions,
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  draft: DraftSubscriber;
  setDraft: (d: DraftSubscriber) => void;
  orgOptions: { id: string; name: string }[];
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const isEdit = Boolean(draft.id);
  // Servicios de LA ORG ELEGIDA EN EL FORM (no la org global del header) —
  // al cambiar el select de organización la lista se refetchea sola porque
  // orgId es parte de la cache key de RTK Query.
  const { data: services = [] } = useGetServicesQuery(
    { orgId: draft.org_id },
    { skip: !draft.org_id }
  );
  const allServices = draft.service_ids == null;
  const hasEmail = draft.channels.includes("email");
  const hasSlack = draft.channels.includes("slack");

  function toggleChannel(channel: "email" | "slack") {
    const active = draft.channels.includes(channel);
    // No permitir sacar el último canal — el backend exige al menos uno.
    if (active && draft.channels.filter((c) => c === "email" || c === "slack").length === 1) return;
    setDraft({
      ...draft,
      channels: active
        ? draft.channels.filter((c) => c !== channel)
        : [...draft.channels, channel],
    });
  }

  function toggleService(id: string) {
    const list = draft.service_ids ?? [];
    const active = list.includes(id);
    setDraft({
      ...draft,
      service_ids: active ? list.filter((x) => x !== id) : [...list, id],
    });
  }

  const selectedCount = (draft.service_ids ?? []).length;
  const orgName = orgOptions.find((o) => o.id === draft.org_id)?.name ?? "—";

  return createPortal(
    <div className="modal-backdrop" onClick={onCancel}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="modal-card"
        style={{
          width: 560,
          maxWidth: "94vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
      >
        {/* ── Cabecera ─────────────────────────────────────────────────── */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid var(--border-1)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: "color-mix(in oklab, var(--accent) 12%, transparent)",
                color: "var(--accent)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icons.Bell size={16} sw={1.7} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontFamily: "var(--font-display)", fontWeight: 700 }}>
                {isEdit ? "Editar suscriptor" : "Nuevo suscriptor"}
              </h2>
              <p className="text-sm text-muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                {isEdit
                  ? `Modificá la configuración de ${draft.email || "este suscriptor"}`
                  : "Quién recibe notificaciones (email y/o Slack) cuando se abre un incidente"}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-icon btn-ghost"
            onClick={onCancel}
            aria-label="Cerrar"
          >
            <Icons.Close size={14} />
          </button>
        </header>

        {/* ── Cuerpo scroleable ────────────────────────────────────────── */}
        <div
          style={{
            overflowY: "auto",
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {/* Sección 1: Organización */}
          <Section icon={<Icons.Cube size={13} sw={1.8} />} title="Organización" hint={
            isEdit
              ? "No se puede cambiar al editar. Borrá y volvé a crear si hace falta."
              : "El suscriptor recibirá notificaciones solo de los servicios de esta org."
          }>
            <select
              className="select"
              value={draft.org_id}
              onChange={(e) =>
                // Al cambiar de org, los service_ids elegidos pertenecen a la
                // org anterior — se limpian (manteniendo el modo manual/todos).
                setDraft({
                  ...draft,
                  org_id: e.target.value,
                  service_ids: draft.service_ids == null ? null : [],
                })
              }
              disabled={isEdit || orgOptions.length <= 1}
              required
            >
              {orgOptions.length === 0 && (
                <option value="" disabled>No hay organizaciones disponibles</option>
              )}
              {orgOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </Section>

          {/* Sección 2: Canales + destinatario + severidad. Email y Slack son
              independientes — cada canal activo exige su destino. */}
          <Section icon={<Icons.Mail size={13} sw={1.8} />} title="Canales de notificación">
            <div style={{ display: "flex", gap: 6, padding: 3, background: "var(--bg-2)", borderRadius: 8 }}>
              <SegmentBtn active={hasEmail} onClick={() => toggleChannel("email")}>
                <Icons.Mail size={12} /> Email
              </SegmentBtn>
              <SegmentBtn active={hasSlack} onClick={() => toggleChannel("slack")}>
                <Icons.Send size={12} /> Slack
              </SegmentBtn>
            </div>
            {hasEmail && (
              <div>
                <label className="label" style={{ marginBottom: 4 }}>Email</label>
                <input
                  className="input"
                  type="email"
                  placeholder="ops@nuevo-leon.gob"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  disabled={isEdit && Boolean(draft.email)}
                  required
                />
                {isEdit && Boolean(draft.email) && (
                  <span className="hint">El email no se puede cambiar.</span>
                )}
              </div>
            )}
            {hasSlack && (
              <div>
                <label className="label" style={{ marginBottom: 4 }}>Slack webhook URL</label>
                <input
                  className="input"
                  type="url"
                  placeholder="https://hooks.slack.com/services/T000/B000/xxxx"
                  value={draft.slack_webhook_url}
                  onChange={(e) => setDraft({ ...draft, slack_webhook_url: e.target.value })}
                  pattern="https://hooks\.slack\.com/.*"
                  required
                />
                <span className="hint">
                  Incoming webhook del workspace — se crea en Slack (apps → Incoming Webhooks) y
                  apunta a un canal fijo. Solo se postea cuando se <strong>abre</strong> un incidente.
                </span>
              </div>
            )}
            <div>
              <label className="label" style={{ marginBottom: 4 }}>Severidad mínima</label>
              <select
                className="select"
                value={draft.severity_min}
                onChange={(e) => setDraft({ ...draft, severity_min: e.target.value })}
              >
                <option value="informational">Informativo</option>
                <option value="minor">Menor</option>
                <option value="major">Mayor</option>
                <option value="critical">Crítico</option>
              </select>
            </div>
            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: "var(--bg-1)",
                border: "1px dashed var(--border-1)",
                borderRadius: 6,
                fontSize: 12.5,
                color: "var(--fg-2)",
              }}
            >
              <Icons.Info size={13} />
              Solo se envía cuando el incidente alcanza esta severidad o superior.
            </div>
          </Section>

          {/* Sección 3: Filtro por servicios */}
          <Section
            icon={<Icons.Filter size={13} sw={1.8} />}
            title="Qué servicios notificar"
            rightChip={
              allServices
                ? "Todos"
                : `${selectedCount} seleccionado${selectedCount === 1 ? "" : "s"}`
            }
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                marginBottom: 8,
                padding: 3,
                background: "var(--bg-2)",
                borderRadius: 8,
              }}
            >
              <SegmentBtn
                active={allServices}
                onClick={() => setDraft({ ...draft, service_ids: null })}
              >
                <Icons.Check size={12} /> Todos los servicios
              </SegmentBtn>
              <SegmentBtn
                active={!allServices}
                onClick={() => setDraft({ ...draft, service_ids: [] })}
              >
                Elegir manualmente
              </SegmentBtn>
            </div>
            {!allServices && (
              <div
                style={{
                  border: "1px solid var(--border-1)",
                  borderRadius: 8,
                  maxHeight: 200,
                  overflowY: "auto",
                  padding: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {services.length === 0 ? (
                  <div style={{ color: "var(--fg-3)", fontSize: 12, padding: 12, textAlign: "center" }}>
                    No hay servicios registrados en {orgName}. Creá uno desde Servicios.
                  </div>
                ) : (
                  services.map((s) => {
                    const checked = (draft.service_ids ?? []).includes(s.id);
                    return (
                      <label
                        key={s.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 10px",
                          borderRadius: 6,
                          fontSize: 13,
                          cursor: "pointer",
                          background: checked
                            ? "color-mix(in oklab, var(--accent) 8%, transparent)"
                            : "transparent",
                          transition: "background 0.12s",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleService(s.id)}
                          style={{ accentColor: "var(--accent)" }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 12,
                              color: "var(--fg-1)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.slug}
                          </div>
                          <div style={{ color: "var(--fg-3)", fontSize: 11.5 }}>{s.name}</div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </Section>

          {error && (
            <div
              style={{
                padding: "10px 14px",
                background: "color-mix(in oklab, var(--st-major) 10%, transparent)",
                border: "1px solid color-mix(in oklab, var(--st-major) 30%, transparent)",
                borderRadius: 6,
                fontSize: 13,
                color: "var(--st-major)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Icons.AlertTriangle size={14} />
              {error}
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <footer
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "14px 20px",
            borderTop: "1px solid var(--border-1)",
            background: "var(--bg-1)",
          }}
        >
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              submitting ||
              (hasEmail && !draft.email) ||
              (hasSlack && !draft.slack_webhook_url)
            }
          >
            {submitting ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear suscriptor"}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
}

// ─── Helpers visuales del form ───────────────────────────────────────────────

function Section({
  icon,
  title,
  hint,
  rightChip,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  rightChip?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11.5,
            fontWeight: 600,
            color: "var(--fg-3)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {icon}
          {title}
        </div>
        {rightChip && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: 999,
              background: "var(--bg-2)",
              border: "1px solid var(--border-1)",
              color: "var(--fg-2)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {rightChip}
          </span>
        )}
      </header>
      {children}
      {hint && <span className="hint" style={{ marginTop: 2 }}>{hint}</span>}
    </section>
  );
}

function SegmentBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "7px 10px",
        background: active ? "var(--bg-0)" : "transparent",
        border: "none",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: 12.5,
        fontWeight: active ? 600 : 500,
        color: active ? "var(--fg-1)" : "var(--fg-3)",
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        transition: "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

function InfoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-head">
          <div>
            <h2>¿Qué son los suscriptores?</h2>
            <div className="text-sm text-muted" style={{ marginTop: 4 }}>
              Destinatarios internos de las notificaciones de incidentes
            </div>
          </div>
          <button type="button" className="btn btn-icon btn-ghost" onClick={onClose}>
            <Icons.Close size={14} />
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-info">
            <div className="modal-info-bullet">
              <div className="modal-info-bullet-icon"><Icons.Flag size={16} /></div>
              <div className="modal-info-bullet-text">
                <h4>Cuándo se notifica</h4>
                Cuando alguien crea un incidente con <strong>"Notificar suscriptores"</strong> marcado,
                cada suscriptor recibe el mensaje público por su canal preferido. También en cada update y al resolverlo.
              </div>
            </div>

            <div className="modal-info-bullet">
              <div className="modal-info-bullet-icon"><Icons.Mail size={16} /></div>
              <div className="modal-info-bullet-text">
                <h4>Canales: email y Slack</h4>
                Email manda un correo transaccional con el resumen del incidente y un link al
                dashboard, en cada etapa (abierto, updates, resuelto). Slack postea en un canal
                (vía incoming webhook) solo cuando el incidente se <strong>abre</strong>. Un
                suscriptor puede tener uno u ambos canales. Webhook genérico y SMS quedan en el backlog.
              </div>
            </div>

            <div className="modal-info-bullet">
              <div className="modal-info-bullet-icon"><Icons.Cube size={16} /></div>
              <div className="modal-info-bullet-text">
                <h4>Filtrar por servicios</h4>
                Por defecto, un suscriptor recibe notificaciones de <strong>todos los servicios</strong> de la organización.
                Podés acotar a un subconjunto (ej: el equipo de pagos recibe sólo incidentes de
                <code className="text-mono" style={{ background: "var(--bg-1)", padding: "1px 6px", borderRadius: 4 }}>pagos.*</code>).
              </div>
            </div>

            <div className="modal-info-bullet">
              <div className="modal-info-bullet-icon"><Icons.AlertTriangle size={16} /></div>
              <div className="modal-info-bullet-text">
                <h4>Severidad mínima</h4>
                El suscriptor sólo recibe incidentes con esa severidad o superior. Por ejemplo,
                un ejecutivo con <code className="text-mono" style={{ background: "var(--bg-1)", padding: "1px 6px", borderRadius: 4 }}>critical</code> sólo va a recibir las caídas mayores —
                no las alertas menores.
              </div>
            </div>

            <div className="modal-info-bullet">
              <div className="modal-info-bullet-icon"><Icons.Check size={16} /></div>
              <div className="modal-info-bullet-text">
                <h4>Verificación de email</h4>
                Al crear un suscriptor por email, le mandamos un link que tiene que clickear.
                Hasta que lo verifique, no recibe notificaciones — sólo aparece como <strong>Pendiente</strong>.
              </div>
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
